import {
  ClearGigsafeUserState,
  CreateGigsafeRole,
  FetchGigsafeRolePermissions,
  FetchGigsafeRoles,
  FetchGigsafeUsers,
  InviteGigsafeUser,
  UpdateGigsafeRole,
} from 'src/constants/actionTypes'
import type { GigsafePermission, GigsafeRole, GigsafeUsersPage } from 'src/interfaces/gigsafeUser'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { GigsafeUserState } from './types'

const getInitialState = (): GigsafeUserState => {
  return {
    users: [],
    usersTotal: 0,
    usersLoading: false,
    usersError: null,
    inviting: false,
    roles: [],
    rolesLoading: false,
    rolesError: null,
    permissions: [],
    permissionsLoading: false,
    savingRole: false,
  }
}

export const gigsafeUserReducer = (
  state: GigsafeUserState = getInitialState(),
  action: Action<unknown>,
): GigsafeUserState => {
  switch (action.type) {
    // Deliberately does NOT clear `users`: paging a table that blanks between pages reads as a
    // fault, and GridTable's own loading overlay covers the stale page.
    case FetchGigsafeUsers.type.REQUEST:
      return {
        ...state,
        usersLoading: true,
        usersError: null,
      }
    case FetchGigsafeUsers.type.SUCCESS: {
      const page = (action.payload as GigsafeUsersPage | undefined) ?? { users: [], total: 0 }

      return {
        ...state,
        users: page.users,
        usersTotal: page.total,
        usersLoading: false,
        usersError: null,
      }
    }
    case FetchGigsafeUsers.type.FAILED:
      return {
        ...state,
        usersLoading: false,
        usersError: resolveErrorMessage(action.payload, 'Failed to load users'),
      }

    // Roles DO clear on request — this only runs on mount and after a mutation, never per page.
    case FetchGigsafeRoles.type.REQUEST:
      return {
        ...state,
        roles: [],
        rolesLoading: true,
        rolesError: null,
      }
    case FetchGigsafeRoles.type.SUCCESS: {
      const roles = (action.payload as GigsafeRole[] | undefined) ?? []

      return {
        ...state,
        roles,
        rolesLoading: false,
        rolesError: null,
      }
    }
    case FetchGigsafeRoles.type.FAILED:
      return {
        ...state,
        rolesLoading: false,
        rolesError: resolveErrorMessage(action.payload, 'Failed to load roles'),
      }

    case FetchGigsafeRolePermissions.type.REQUEST:
      return {
        ...state,
        permissionsLoading: true,
      }
    case FetchGigsafeRolePermissions.type.SUCCESS: {
      const permissions = (action.payload as GigsafePermission[] | undefined) ?? []

      return {
        ...state,
        permissions,
        permissionsLoading: false,
      }
    }
    case FetchGigsafeRolePermissions.type.FAILED:
      return {
        ...state,
        permissionsLoading: false,
      }

    case InviteGigsafeUser.type.REQUEST:
      return {
        ...state,
        inviting: true,
      }
    case InviteGigsafeUser.type.SUCCESS:
    case InviteGigsafeUser.type.FAILED:
      return {
        ...state,
        inviting: false,
      }

    // Create and update never write to `roles` — the list is refetched on success, which is the
    // single source of truth. See the design doc's §6.4.
    case CreateGigsafeRole.type.REQUEST:
    case UpdateGigsafeRole.type.REQUEST:
      return {
        ...state,
        savingRole: true,
      }
    case CreateGigsafeRole.type.SUCCESS:
    case CreateGigsafeRole.type.FAILED:
    case UpdateGigsafeRole.type.SUCCESS:
    case UpdateGigsafeRole.type.FAILED:
      return {
        ...state,
        savingRole: false,
      }

    // Every field goes back to its initial value, not just `users`: roles, the assignable
    // permission list and the error strings are all scoped to one customer too, and a partial
    // reset would leave the previous customer's roles feeding the invite form's Role select.
    case ClearGigsafeUserState.type:
      return getInitialState()

    default:
      return state
  }
}
